import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, watch } from 'vue'

import Message from '@/client/components/_atoms/Message.vue'
import UrlInput, { UrlInputItem } from '@/client/components/_molecules/url_input'
import XPostPreview from '@/client/components/_molecules/x_post_preview'
import { CAMPAIGN_STATUS } from '@/client/constant'

import { CampaignTweetPostSaveResult, useCampaignTweetPostStore } from '../../hooks/useCampaignTweetPostStore'
import { findDuplicateUrls, validateTweetPostUrl } from '../../utils/tweetPostUrlValidation'

interface Emits {
  (e: 'close'): void
  (e: 'saved'): void
}

type LocalUrlRow = {
  value: string
  deletable: boolean
  postId?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CampaignTweetPostDialog',
  props: {
  visible: {
    type: Boolean,
    required: true
  },
  campaignId: {
    type: Number,
    default: null
  },
  campaignTitle: {
    type: String,
    default: ''
  },
  status: {
    type: Number,
    default: undefined
  },
  hasTweetPostUrl: {
    type: Boolean,
    default: false
  },
  // 開催アカウント名。URL の screen_name 一致チェックに使用する。
  screenName: {
    type: String,
    default: ''
  }
},
  emits: ["close", "saved"],
  setup(__props, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'saved'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const { proxy } = getCurrentInstance() as any





const store = useCampaignTweetPostStore()

// 入力中 URL 群（ローカル state）。value は URL 文字列、error/warning は行ごとの検証結果。
const rows = ref<LocalUrlRow[]>([])
const previewPage = ref(1)

const isLocked = computed(() => store.isLocked)
const fetching = computed(() => store.fetching)
const saving = computed(() => store.saving)
// キャンペーン投稿 URL の追加上限（グループ単位の設定値）。これを超えて行を追加できない。
const maxTweetPostUrlCount = computed(() => store.maxTweetPostUrlCount)

const urlHelpText = computed(
  () =>
    `登録できるURLはキャンペーン実施アカウントのポストのみです（最大${maxTweetPostUrlCount.value}件）。URLに付いているパラメータ（「?」以降）は自動で削除されます。`
)

// 最終集計中は追加・削除ともに不可（閲覧のみ）。上限に達した場合も追加不可。
const canAdd = computed(() => !isLocked.value && rows.value.length < maxTweetPostUrlCount.value)
const canDelete = computed(() => !isLocked.value)

const isCollectionCompleted = computed(() => props.status === CAMPAIGN_STATUS.COLLECTION_COMPLETED)
const isBeforeStart = computed(() => props.status === CAMPAIGN_STATUS.BEFORE_EVENT)

// 開催中は URL 設定の有無（初回収集の完了状況）で文言を変えず、同じ案内を表示する。
const noteText = computed(() => {
  if (isLocked.value) {
    return 'キャンペーンは最終集計中のため、URLの追加・削除はできません。'
  }
  if (isCollectionCompleted.value) {
    return 'キャンペーンは集計完了のため、集計前に設定したURLは削除できません。URLの追加はできます。'
  }
  if (isBeforeStart.value) {
    return 'キャンペーン投稿URLを保存すると、キャンペーン開始後30分以内に応募の収集が始まります。応募は30分間隔で収集されます。'
  }
  return 'キャンペーン投稿URLを保存すると、30分以内に応募の収集が始まります。応募は30分間隔で収集され、URLの設定時点から7日より前の応募は収集されません。'
})

// 行ごとの検証結果を付与した UrlInput 用モデル。
const urlItems = computed<UrlInputItem[]>(() => {
  const duplicates = findDuplicateUrls(rows.value.map(row => row.value))

  return rows.value.map(row => {
    const item: UrlInputItem = {
      value: row.value,
      editable: row.deletable && !isLocked.value,
      deletable: row.deletable
    }

    if (!item.editable || !row.value.trim()) {
      return item
    }

    if (duplicates.includes(row.value)) {
      item.error = 'この URL はすでに追加されています。'
      return item
    }

    const result = validateTweetPostUrl(row.value, props.screenName)
    if (result.valid) {
      return item
    }

    if (result.reason === 'format' || result.reason === 'empty') {
      item.error = 'URL の形式が正しくありません'
      return item
    }

    item.error = `実施アカウント@${props.screenName}と異なる投稿URLです。`
    return item
  })
})

// 保存対象の有効な URL（空行を除く）。
const filledUrls = computed(() => rows.value.map(row => row.value.trim()).filter(value => value.length > 0))

// UI バリデーションを通過しているか（エラー・警告が 1 つもない）。
const isAllValid = computed(() => urlItems.value.every(item => !item.error && !item.warning))

// プレビュー対象の post_id（全行分、空行は null）。
const previewPostIds = computed(() =>
  rows.value.map(row => {
    if (!row.value.trim()) {
      return null
    }
    const result = validateTweetPostUrl(row.value, props.screenName)
    return result.valid ? extractPostId(row.value) : null
  })
)

const extractPostId = (url: string): string | null => {
  const matched = url.trim().match(/\/status\/(\d+)/)
  return matched ? matched[1] : null
}

// 最終集計中は保存不可。バリデーション未通過は保存不可。
const isSaveButtonDisabled = computed(() => {
  if (isLocked.value) return true
  if (saving.value) return true
  return !isAllValid.value
})

const resolveMode = (): 'create' | 'edit' => (props.hasTweetPostUrl ? 'edit' : 'create')

// store の urls / postIdByUrl（唯一の情報源）から入力行を再構築する。
// 集計完了前に設定した URL は deletable=false（編集・削除ともに不可）、0 件かつ新規モードは空行を 1 つ用意する。
const syncRowsFromStore = () => {
  if (store.urls.length > 0) {
    rows.value = store.urls.map(url => ({
      value: url,
      deletable: store.deletableUrls.includes(url),
      postId: store.postIdByUrl.get(url)
    }))
    return
  }
  rows.value = store.mode === 'create' ? [{ value: '', deletable: true }] : []
}

const loadTweetPosts = async (campaignId: number) => {
  store.setMode(resolveMode())
  store.setLocked(props.status === CAMPAIGN_STATUS.DURING_FINAL_COLLECTION)

  try {
    await store.fetchTweetPosts(campaignId)
    syncRowsFromStore()
  } catch {
    proxy.$notify({
      title: '応募収集URLの取得に失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  }
}

const handleUpdate = ({ index, value }: { index: number; value: string }) => {
  const next = [...rows.value]
  next[index] = { ...next[index], value }
  rows.value = next
  // 入力に追従して該当行のプレビューページへ
  previewPage.value = index + 1
}

// 「+ URL を追加」: 新規は空行追加。編集は空行を追加し、確定（フォーカスアウト相当）は保存時にまとめて POST。
const handleAdd = () => {
  rows.value = [...rows.value, { value: '', deletable: true }]
  // 追加後、新しい行のプレビューページへ
  previewPage.value = rows.value.length
}

// 削除は即時 API を呼ばず、行をローカルから除くだけ。保存ボタン押下時にまとめて反映する。
const handleDelete = (index: number) => {
  rows.value = rows.value.filter((_row, i) => i !== index)
  if (rows.value.length === 0 && store.mode === 'create') {
    rows.value = [{ value: '', deletable: true }]
  }
}

const handleSave = async () => {
  if (!props.campaignId || !isAllValid.value) return

  const targetUrls = filledUrls.value

  // 新規・編集とも、現在ダイアログに残っている URL 全件を送信する。
  // サーバ側は送信集合に一致させる差分同期のため、これで追加・削除がまとめて反映される。
  const result: CampaignTweetPostSaveResult = await store.save(props.campaignId, targetUrls)

  if (result.ok) {
    proxy.$notify({
      title: '応募収集URLを保存しました。',
      customClass: 'success',
      duration: 5000
    })

    // 保存内容を DB から再取得して store（urls / postIdByUrl）を最新化し、UI にも即時反映する。
    if (props.campaignId) {
      try {
        await store.fetchTweetPosts(props.campaignId)
        syncRowsFromStore()
      } catch {
        // 再取得失敗は保存自体の成功を妨げない。次回オープン時に再取得される。
      }
    }

    emit('saved')
    emit('close')
    return
  }

  if (result.type === 'NOT_MATCH') {
    proxy.$notify({
      title: '実施アカウントと一致しない URL があります。',
      customClass: 'danger',
      duration: 5000
    })
    return
  }

  if (result.type === 'MAX_TWEET_POST_URL_COUNT_OVER') {
    proxy.$notify({
      title: `登録できるURLは最大${maxTweetPostUrlCount.value}件です。`,
      customClass: 'danger',
      duration: 5000
    })
    return
  }

  // 投稿 URL の形式不正はその旨を通知する。
  if (result.type === 'PARAMETER_INVALID') {
    proxy.$notify({
      title: '投稿URLの形式が不正です。',
      customClass: 'danger',
      duration: 5000
    })
    return
  }

  proxy.$notify({
    title: '応募収集URLの保存に失敗しました。恐れ入りますが、時間をおいて再度お試しください。',
    customClass: 'danger',
    duration: 5000
  })
}

const handleClose = () => {
  emit('close')
}

const handlePreviewPageChange = (page: number) => {
  previewPage.value = page
}

watch(
  () => [props.visible, props.campaignId] as const,
  ([visible, campaignId]) => {
    if (visible && campaignId) {
      previewPage.value = 1
      rows.value = []
      loadTweetPosts(campaignId as number)
      return
    }

    if (!visible) {
      store.reset()
      rows.value = []
      previewPage.value = 1
    }
  }
)

return { __sfc: true,proxy, props, emit, store, rows, previewPage, isLocked, fetching, saving, maxTweetPostUrlCount, urlHelpText, canAdd, canDelete, isCollectionCompleted, isBeforeStart, noteText, urlItems, filledUrls, isAllValid, previewPostIds, extractPostId, isSaveButtonDisabled, resolveMode, syncRowsFromStore, loadTweetPosts, handleUpdate, handleAdd, handleDelete, handleSave, handleClose, handlePreviewPageChange, Message, UrlInput, XPostPreview }
}

})